import { Carousel, Typography, Image, Space, Button } from "antd";
import { FC, useCallback, useContext, useMemo, useRef } from "react";
import {
  View,
  useAppContext,
  useClientContext,
  useField,
  useNavigation,
  useTableHeaders
} from "@data-root/react";
import { SchemaItemProps, UseRowContext } from "@data-root/react";
import {
  AppSpecifier,
  RefSchemaItem,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import CarouselArrows from "../../CarouselArrows";
import { useQueries } from "@tanstack/react-query";
import { EditOutlined, RightOutlined } from "@ant-design/icons";

interface CarouselItemProps {
  appSpecifier: AppSpecifier;
  tableId: string;
  row: RowWithEmbeddedMetadata;
  onClick: (row: RowWithEmbeddedMetadata) => void;
  onEdit: (row: RowWithEmbeddedMetadata) => void;
}

const { Title, Text } = Typography;

const CarouselItem: FC<CarouselItemProps> = ({
  appSpecifier,
  tableId,
  row,
  onClick,
  onEdit,
}) => {
  const {
    primaryHeader,
    secondaryHeader,
    imageUri: image,
  } = useTableHeaders(appSpecifier, tableId, row);

  return (
    <div>
      <View>
        <Image
          style={{
            backgroundColor: "#dddddd",
            height: 200,
            width: "100%",
            maxHeight: 200,
            objectFit: "cover",
          }}
          src={image}
        />

        <Title level={5}>{primaryHeader}</Title>

        <Text>{secondaryHeader}</Text>

        <Space.Compact>
          <Button icon={<RightOutlined />} onClick={() => onClick(row)} />
          <Button icon={<EditOutlined />} onClick={() => onEdit(row)} />
        </Space.Compact>
      </View>
    </div>
  );
};

export const RefDetailCarouselPresentation: FC<
  SchemaItemProps<RefSchemaItem>
> = ({ item }) => {
  const { core } = useClientContext();
  const { appSpecifier } = useAppContext();

  const { rowId } = useContext(UseRowContext)!;
  const { value } = useField(item);

  const { navigation } = useNavigation();

  const carouselRef = useRef();

  const name = useMemo<string>(() => item.name, [item.name]);

  const multiple = useMemo(() => item.multiple || false, [item.multiple]);

  const isNullish = useMemo<boolean>(() => {
    // NOTE: We need to check for null and undefined because of the way we store
    return value === null || !value;
  }, [value]);

  const values = useMemo<string[]>(() => {
    if (isNullish) return [];
    return (multiple ? value : [value]) as string[];
  }, [isNullish, multiple, value]);

  const { rows, ready } = useQueries({
    queries: values.map((rowId) => ({
      queryKey: ["row", appSpecifier, item.ref, rowId],
      queryFn: async () => await core.get(appSpecifier, item.ref, rowId),
    })),
    combine: (results) => ({
      rows: results.map((result) => result.data),
      ready: results.every((result) => result.status === "success"),
    }),
  });

  const onClick = useCallback(
    (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.setParams({ rowId } as any);
      appNavigation.navigate(item.detailViewId!, {
        rowId: refRow.id,
      });
    },
    [rowId, item.detailViewId, navigation]
  );

  const onEdit = useCallback(
    (refRow: RowWithEmbeddedMetadata) => {
      const appNavigation = navigation.getParent("app" as any)!;
      appNavigation.setParams({ rowId } as any);
      appNavigation.navigate(item.formViewId!, {
        rowId: refRow.id,
      });
    },
    [rowId, item.formViewId, navigation]
  );

  return (
    <View>
      <Title level={5}>{name}</Title>

      {/* <Carousel autoplay> */}
      <CarouselArrows carouselRef={carouselRef} />
      <Carousel ref={carouselRef as any}>
        {rows.map(
          (row, index) =>
            index % 2 === 0 && (
              <div className="carousel-row" key={row.id}>
                <CarouselItem
                  appSpecifier={appSpecifier}
                  tableId={item.ref}
                  row={row}
                  // primaryHeader={refTable.label}
                  // secondaryHeader={refTable.secondaryLabel}
                  // image={refTable.imageLabel}
                  onClick={onClick}
                  onEdit={onEdit}
                />
                {rows[index + 1] && (
                  <CarouselItem
                    appSpecifier={appSpecifier}
                    tableId={item.ref}
                    row={rows[index + 1]}
                    // primaryHeader={refTable.label}
                    // secondaryHeader={refTable.secondaryLabel}
                    // image={refTable.imageLabel}
                    onClick={onClick}
                    onEdit={onEdit}
                  />
                )}
              </div>
            )
        )}
      </Carousel>
    </View>
  );
};
